import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios'; // Make sure you have run: npm install axios

import { Helmet } from 'react-helmet-async';
import './ForumAdmin.css';
import { Search, ArrowLeft, ThumbsUp, MessageCircle, Trash2, AlertCircle } from 'lucide-react';

const API_URL =  process.env.REACT_APP_API_URL;  

// API configuration
const api = axios.create({
    baseURL: `${API_URL}/api`, // Your backend URL

    headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
    }
});

// --- Sub-component for the Post List View ---
const PostListView = ({ posts, users, onSelectPost, onDeletePost, searchQuery, setSearchQuery }) => (
    <div className="admin-section">
        <h2 className="admin-section-header">Forum Post Management</h2>
        <div className="table-controls">
            <div className="search-container">
                <Search className="search-icon" size={20} />
                <input type="text" placeholder="Search posts by title or author..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="table-search-input" />
            </div>
        </div>
        <div className="table-responsive">
            <table className="data-table">
                <thead>
                    <tr>
                        <th>Author</th>
                        <th>Post Title</th>
                        <th>Replies</th>
                        <th>Created</th>
                        <th>Last Updated</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {posts.map(post => {
                        const author = users[post.authorId];
                        const lastUpdated = post.updatedAt ? new Date(post.updatedAt) : new Date(post.createdAt);
                        return (
                            <tr key={post._id} onClick={() => onSelectPost(post)} className="clickable-row">
                                <td>
                                    <div className="user-cell"><strong>{author?.fullName || 'Unknown'}</strong><span>{author?.email || 'N/A'}</span></div>
                                </td>
                                <td>{post.title}</td>
                                <td className="centered-text">{post.comments?.length || 0}</td>
                                <td>{new Date(post.createdAt).toLocaleDateString()}</td>
                                <td>{lastUpdated.toLocaleDateString()}</td>
                                <td><button onClick={(e) => { e.stopPropagation(); onDeletePost(post._id); }} className="action-btn delete"><Trash2 size={16} /> Delete</button></td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    </div>
);

// --- Sub-component for the Post Detail View ---
const PostDetailView = ({ post, author, usersData, onBack }) => {
    const comments = post.comments || [];

    return (
        <div className="admin-section">
            <button onClick={onBack} className="back-button"><ArrowLeft size={20} /> Back to All Posts</button>
            <div className="post-detail-card">
                <div className="post-header"><div className="post-author-avatar">{author?.fullName?.charAt(0) || 'U'}</div><div><div className="post-author-name">{author?.fullName || 'Unknown User'}</div><div className="post-timestamp">Posted on {new Date(post.createdAt).toLocaleString()}</div></div></div>
                <h2 className="post-title-modal">{post.title}</h2>
                {post.attachments?.[0]?.fileUrl && <img src={post.attachments[0].fileUrl} alt="Post attachment" className="post-image-detail" />}
                <p className="post-content-modal">{post.content}</p>
                <div className="post-stats"><span><ThumbsUp size={16} /> {post.reactions.length} Upvotes</span><span><MessageCircle size={16} /> {comments.length} Comments</span></div>
                <div className="comments-section">
                    <h3 className="comments-header">Comments</h3>
                    {comments.map(comment => {
                        const cAuthor = usersData[comment.authorId];
                        return (
                            <div key={comment._id} className="comment-container"><div className="comment-author-avatar">{cAuthor?.fullName?.charAt(0) || 'U'}</div><div className="comment-body"><span className="comment-author-name">{cAuthor?.fullName || 'Unknown'}</span><p className="comment-text">{comment.content}</p></div></div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

// --- Main ForumAdmin Component ---
const ForumAdmin = () => {
    const [posts, setPosts] = useState([]);
    const [usersData, setUsersData] = useState({});
    const [selectedPost, setSelectedPost] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const postsRes = await api.get('/forum/posts');
                const fetchedPosts = postsRes.data;

                const authorIds = new Set();
                fetchedPosts.forEach(post => {
                    if (post.authorId) authorIds.add(post.authorId);
                    post.comments?.forEach(comment => {
                        if (comment.authorId) authorIds.add(comment.authorId);
                    });
                });

                const userPromises = [...authorIds].map(id => 
                    api.get(`/admin/users/${id}`).catch(() => null)
                );
                const userResults = await Promise.all(userPromises);
                
                const usersMap = {};
                userResults
                    .filter(Boolean)
                    .forEach(res => { 
                        if (res.data && res.data.userId) {
                            usersMap[res.data.userId] = res.data;
                        }
                    });
                
                setUsersData(usersMap);
                setPosts(fetchedPosts);

            } catch (err) {
                console.error("Failed to fetch forum data:", err);
                setError("Could not load forum data. Please check the network connection and try again.");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleDeletePost = async (postId) => {
        if (window.confirm('Are you sure you want to delete this post and all its comments? This action cannot be undone.')) {
            try {
                await api.delete(`/forum/posts/${postId}`);
                setPosts(currentPosts => currentPosts.filter(p => p._id !== postId));
                if (selectedPost?._id === postId) {
                    setSelectedPost(null);
                }
                alert('Post deleted successfully.');
            } catch (err) {
                alert('Failed to delete post. Please try again.');
                console.error('Delete error:', err);
            }
        }
    };
    
    // --- FIX: Added safety checks to prevent 'toLowerCase' on undefined ---
    const filteredPosts = useMemo(() =>
        posts.filter(post => {
            const author = usersData[post.authorId];
            const lowerCaseQuery = searchQuery.toLowerCase();

            const titleMatch = post.title && post.title.toLowerCase().includes(lowerCaseQuery);
            const authorMatch = author && author.fullName && author.fullName.toLowerCase().includes(lowerCaseQuery);

            return titleMatch || authorMatch;
        }), [posts, searchQuery, usersData]);


      if (loading) {
        return (
            <>
                <Helmet>
                    <title>Loading Forum Data...</title>
                </Helmet>
                <div className="loading-container"> Loading Forum Data...</div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Helmet>
                    <title>Error - Forum Management</title>
                </Helmet>
                <div className="error-message"><AlertCircle/> {error}</div>
            </>
        );
    }

    return (
        <div className="forum-admin-page">
             <Helmet>
                <title>
                    {selectedPost
                        ? `Viewing Post: "${selectedPost.title}" | GN VISA Admin`
                        : 'Forum Management | GN VISA Admin'
                    }
                </title>
                <meta name="description" content="Moderate and manage all forum posts and user discussions." />
            </Helmet>

            {selectedPost ? (
                <PostDetailView 
                    post={selectedPost} 
                    author={usersData[selectedPost.authorId]} 
                    usersData={usersData}
                    onBack={() => setSelectedPost(null)} 
                />
            ) : (
                <PostListView 
                    posts={filteredPosts} 
                    users={usersData} 
                    onSelectPost={setSelectedPost} 
                    onDeletePost={handleDeletePost} 
                    searchQuery={searchQuery} 
                    setSearchQuery={setSearchQuery} 
                />
            )}
        </div>
    );
};

export default ForumAdmin;